import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { FieldType, IEventMetadata, IFormFieldMetadata, IFormMetadata, IFormSectionMetadata, IFormVersionMetadata, ISourceData, ISourceDataConfiguration, ISourceDataConfigurationEvent, ISourceDataConfigurationForm, ISourceDataConfigurationFormSection, ISourceDataConfigurationFormVersion, ISourceDataEvent, ISourceDataField, ISourceDataForm, ITrialFormMetadata, SourceDataPriority } from '@/api/dtos';
import { BasicAlert, Checkbox, CollapsiblePanel, ICollapsiblePanelComponentProps, InputOnlyField, Markdown, Select } from '@ngt/forms';
import { alpha, Box, Button, Chip, Divider, styled, Tooltip, Typography, lighten, darken, TextField, MenuItem, Paper, IconButton, CircularProgress } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays, faCheck, faClipboard, faClipboardCheck, faClockRotateLeft, faCodeBranch, faCommentAlt, faFlag, faInfoSquare, faMessageLines, faRepeat, faSquareList } from '@fortawesome/duotone-light-svg-icons';
import { faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons';
import { faCheckCircle, faExclamationCircle, faExclamationTriangle, faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { ISourceDataFormFieldComponentProps } from '@/utilities/forms/SourceDataFormProcessor';
import SourceDataNotes from './SourceDataNotes';
import { useFormActions } from '@ngt/forms-core';
import SourceDataFormFieldReviewCheckbox from './SourceDataFormFieldReviewCheckbox';
import SourceDataFormFieldVerifyCheckbox from './SourceDataFormFieldVerifyCheckbox';
import SourceDataAuditHistory from './SourceDataAuditHistory';
import SourceDataFormFieldValue from './SourceDataFormFieldValue';
import { faExclamationTriangle as faExclamationTriangleSolid, faTriangle } from '@fortawesome/pro-solid-svg-icons';

const Content = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2, 2, 2, 2),
    display: 'flex',
    alignItems: 'flex-start'
}));

const FieldLabel = styled(Box)(({ theme }) => ({
    minWidth: 200,
    minHeight: 40,
    padding: theme.spacing(1, 1, 1, 1)
}));

const PriorityContainer = styled(Box)(({ theme }) => ({
    marginRight: theme.spacing(2),
    marginTop: 8
}));

const FormValueContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    marginLeft: 'auto',
    alignItems: 'flex-start'
}));

const Reviewed = styled(Box)(({ theme }) => ({
    marginLeft: theme.spacing(2),
    minWidth: 120
}));

const ReviewedCheckbox = styled(Box)(({ theme }) => ({
    '& > div > div > label': {
        marginLeft: 0,
        marginRight: theme.spacing(1)
    },
    '& > div > div > label > span': {
        padding: 0
    }
}));

const CheckboxIcon = styled(Box)(({ theme }) => ({
    position: 'relative',
    padding: theme.spacing(1),
    minWidth: 0,
    height: 40,
    width: 40,
    border: `1px solid ${theme.palette.primary.main}`
}));

const CheckboxCheckIcon = styled(Box)(({ theme }) => ({
    position: 'absolute',
    fontSize: '2em',
    color: theme.palette.success.main
    //color: theme.palette.common.white
}));

const ActionButton = styled(Button)(({ theme }) => ({
    minWidth: '40px !important',
    height: 40,
    width: 40,
    padding: 0,
    marginLeft: theme.spacing(2)
}));

const ActionChip = styled(Chip)(({ theme }) => ({
    position: 'absolute',
    top: -2,
    right: -2,
    transform: 'scale(0.5)',
    background: theme.palette.mode === 'dark' ? 
        lighten(theme.palette.common.black, 0.15) :
        darken(theme.palette.common.white, 0.01)
}));

const CtcaeTooltip = styled(Box)(({ theme }) => ({
    margin: theme.spacing( -1),
}));

const CtcaeTooltipBox = styled(Box)(({ theme }) => ({
    margin: theme.spacing(0, -1),
    padding: theme.spacing(2, 3, 2, 3)
}));





const SourceDataFormField: FC<ISourceDataFormFieldComponentProps> = (props) => {
    const {
        eventMetadata,
        eventRepeat,
        formMetadata,
        formSectionRepeat,
        formFieldMetadata,
        formFieldPropertyPath,
        formFieldLabel,
        formFieldValue,
        formFieldConfiguration,
        formFieldSourceData,
        formSectionMetadata,
        formFieldValueConfiguration,
        readOnly,
        audits,
        settings,
        trial,
        patient,
        institution,
        hideVerification,
        sourceData
    } = props;
    
    const [notesOpen, setNotesOpen] = useState(false);
    const [auditOpen, setAuditOpen] = useState(false);
    const actions = useFormActions();
    const [loading, setLoading] = useState(false);

    return (
        <>
            <Content>
                {
                    !hideVerification && (
                        <>
                            <ReviewedCheckbox sx={{ visibility:  formFieldConfiguration?.allowSourceDataVerification && formFieldConfiguration?.allowReview ? 'visible' : 'hidden'}}>
                                <Tooltip title="Reviewed">
                                    <div>
                                        <InputOnlyField
                                            propertyPath={`${formFieldPropertyPath}.reviewed`} 
                                            component={SourceDataFormFieldReviewCheckbox}
                                            formFieldPropertyPath={formFieldPropertyPath}
                                            loading={loading}
                                            setLoading={setLoading as any}
                                            disabled={readOnly}
                                            sourceDataId={sourceData?.id}
                                            monitoringVisitId={sourceData?.monitoringVisitId}
                                        />
                                    </div>
                                </Tooltip>
                            </ReviewedCheckbox>
                            <ReviewedCheckbox sx={{ visibility:  formFieldConfiguration?.allowSourceDataVerification && formFieldConfiguration?.allowVerify ? 'visible' : 'hidden'}}>
                                <Tooltip title="Verified">
                                    <div>
                                        <InputOnlyField
                                            propertyPath={`${formFieldPropertyPath}.verified`}
                                            component={SourceDataFormFieldVerifyCheckbox}
                                            formFieldPropertyPath={formFieldPropertyPath}
                                            loading={loading}
                                            setLoading={setLoading as any}
                                            disabled={readOnly}
                                            sourceDataId={sourceData?.id}
                                            monitoringVisitId={sourceData?.monitoringVisitId}
                                        />
                                    </div>
                                </Tooltip>
                            </ReviewedCheckbox>
                        </>
                    )
                }
                <FieldLabel>{formFieldLabel}</FieldLabel>
                <FormValueContainer>
                    {
                        formFieldSourceData?.priority === SourceDataPriority.Low && (
                            <Tooltip title="Priority: Low">
                                <PriorityContainer sx={{color: 'success.main', transform: 'rotate(180deg)'}}>
                                    <FontAwesomeIcon icon={faTriangle} />
                                </PriorityContainer>
                            </Tooltip>
                        )
                    }
                    {
                        formFieldSourceData?.priority === SourceDataPriority.High && (
                            <Tooltip title="Priority: High">
                                <PriorityContainer sx={{color: 'warning.main'}}>
                                    <FontAwesomeIcon icon={faTriangle} />
                                </PriorityContainer>
                            </Tooltip>
                        )
                    }
                    {
                        formFieldSourceData?.priority === SourceDataPriority.Critical && (
                            <Tooltip title="Priority: Critical">
                                <PriorityContainer sx={{color: 'error.main'}}>
                                    <FontAwesomeIcon icon={faExclamationTriangleSolid} />
                                </PriorityContainer>
                            </Tooltip>
                        )
                    }
                    <SourceDataFormFieldValue fieldType={formFieldMetadata?.fieldType} formFieldValue={formFieldValue} />
                </FormValueContainer>
                {
                    settings?.showAudit && (
                        <Tooltip title="Audit History">
                            <span>
                                <ActionButton
                                    size="large"
                                    variant="contained"
                                    color="secondary"
                                    disabled={!audits?.length}
                                    sx={{visibility:  formFieldConfiguration?.showAudit ? 'visible' : 'hidden'}}
                                    onClick={() => setAuditOpen(true)}
                                >
                                    <FontAwesomeIcon icon={faClockRotateLeft} />
                                    <ActionChip label={audits?.length ?? 0} size="small" />
                                </ActionButton>
                            </span>
                        </Tooltip>
                    )
                }
                {
                    settings?.showNotes && (
                        <Tooltip title="Discrepancy Notes">
                            <span>
                                <ActionButton
                                    size="large"
                                    variant="contained"
                                    color="secondary"
                                    disabled={!formFieldSourceData?.notes?.length}
                                    sx={{visibility:  formFieldConfiguration?.showNotes ? 'visible' : 'hidden'}}
                                    onClick={() => setNotesOpen(true)}
                                >
                                    <FontAwesomeIcon icon={faFlag} />
                                    <ActionChip label={formFieldSourceData?.notes?.length ?? 0} size="small" />
                                </ActionButton>
                            </span>
                        </Tooltip>
                    )
                }
            </Content>
            <SourceDataNotes notes={formFieldSourceData?.notes} item={props} open={notesOpen} onClose={() => setNotesOpen(false)} />
            <SourceDataAuditHistory audits={audits} item={props} open={auditOpen} onClose={() => setAuditOpen(false)} />
            { 
                !!formFieldConfiguration?.alertInformation && (
                    <BasicAlert
                        description={<Markdown.Preview>{formFieldConfiguration.alertInformation}</Markdown.Preview>}
                        severity="info"
                        icon={faInfoCircle}
                    />
                )
            }
            { 
                !!formFieldConfiguration?.alertWarning && (
                    <BasicAlert
                        description={<Markdown.Preview>{formFieldConfiguration.alertWarning}</Markdown.Preview>}
                        severity="warning"
                        icon={faExclamationCircle}
                    />
                )
            }
            { 
                !!formFieldConfiguration?.alertError && (
                    <BasicAlert
                        description={<Markdown.Preview>{formFieldConfiguration.alertError}</Markdown.Preview>}
                        severity="error"
                        icon={faExclamationTriangle}
                    />
                )
            }
            { 
                !!formFieldConfiguration?.alertSuccess && (
                    <BasicAlert
                        description={<Markdown.Preview>{formFieldConfiguration.alertSuccess}</Markdown.Preview>}
                        severity="success"
                        icon={faCheckCircle}
                    />
                )
            }
            { 
                !!formFieldValueConfiguration?.alertInformation && (
                    <BasicAlert
                        description={<Markdown.Preview>{formFieldValueConfiguration.alertInformation}</Markdown.Preview>}
                        severity="info"
                        icon={faInfoCircle}
                    />
                )
            }
            { 
                !!formFieldValueConfiguration?.alertWarning && (
                    <BasicAlert
                        description={<Markdown.Preview>{formFieldValueConfiguration.alertWarning}</Markdown.Preview>}
                        severity="warning"
                        icon={faExclamationCircle}
                    />
                )
            }
            { 
                !!formFieldValueConfiguration?.alertError && (
                    <BasicAlert
                        description={<Markdown.Preview>{formFieldValueConfiguration.alertError}</Markdown.Preview>}
                        severity="error"
                        icon={faExclamationTriangle}
                    />
                )
            }
            { 
                !!formFieldValueConfiguration?.alertSuccess && (
                    <BasicAlert
                        description={<Markdown.Preview>{formFieldValueConfiguration.alertSuccess}</Markdown.Preview>}
                        severity="success"
                        icon={faCheckCircle}
                    />
                )
            }
        </>
    );
}

export default SourceDataFormField;